// Attendance — Daily Report.
//
// Pick any date (prev / next arrows), filter by status, search, and export the
// day to an Excel sheet. Clicking a row opens that employee's month.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, TextField, IconButton, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import DashboardLayout from "../../layouts/DashboardLayout";
import FlatCard from "../../components/FlatCard";
import { useToast } from "../../components/ToastProvider";
import { useAttendanceDaily } from "../../hooks/queries";
import { exportToExcel } from "../../utils/excelExport";
import { COLORS } from "../../theme/tokens";
import {
  PageHeader,
  AttendanceError,
  DataTable,
  EmployeeCell,
  StatusChip,
  FilterChips,
  ExportButton,
  PunchSummary,
  firstPunchOf,
  lastPunchOf,
  middlePunches,
  useEmployeeEditor,
  InOutCount,
  fmtTime,
  fmtMinutes,
  fmtDate,
  fmtClock,
  ymd,
  parseYmd,
  fieldSx,
  iconBtnSx,
  controlFieldSx,
  dailyRowsForExcel,
  DAILY_EXCEL_COLUMNS,
  dailyCellFill,
} from "./attendanceUi";

const FILTERS = {
  all: () => true,
  present: (r) => ["present", "in_office", "outside"].includes(r.status),
  half_day: (r) => r.status === "half_day",
  late: (r) => r.isLate,
  early: (r) => r.isEarly,
  absent: (r) => r.status === "absent" || r.status === "not_in",
  missed: (r) => r.status === "missed_punch",
};

const shiftDate = (date, delta) => {
  const d = parseYmd(date);
  d.setDate(d.getDate() + delta);
  return ymd(d);
};

function DailyReport() {
  const navigate = useNavigate();
  const toast = useToast();
  const today = ymd(new Date());
  const [date, setDate] = useState(today);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const editor = useEmployeeEditor();
  const { data, isLoading, error, refetch, isFetching } = useAttendanceDaily(date);

  const rows = useMemo(() => data?.rows || [], [data]);
  const rules = data?.rules;

  const counts = useMemo(
    () => Object.fromEntries(Object.entries(FILTERS).map(([k, fn]) => [k, rows.filter(fn).length])),
    [rows]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(FILTERS[filter])
      .filter((r) => !q || `${r.name} ${r.employeeCode} ${r.department}`.toLowerCase().includes(q));
  }, [rows, filter, search]);

  async function handleExport() {
    setExporting(true);
    try {
      await exportToExcel({
        fileName: `Attendance-Daily-${date}.xlsx`,
        sheets: [
          {
            name: date,
            title: `Daily Attendance — ${fmtDate(date)}`,
            subtitle: rules ? `Shift ${fmtClock(rules.shiftStart)} – ${fmtClock(rules.shiftEnd)} · Grace ${rules.graceMinutes} min · ${visible.length} employees` : "",
            columns: DAILY_EXCEL_COLUMNS,
            rows: dailyRowsForExcel(visible),
            cellFill: dailyCellFill,
          },
        ],
      });
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const columns = [
    { key: "sn", label: "#", width: "40px", render: (_, i) => <Typography sx={{ fontSize: 13, color: COLORS.faint }}>{i + 1}</Typography> },
    { key: "emp", label: "Employee", width: "minmax(170px, 1.6fr)", align: "left", render: (r) => <EmployeeCell name={r.name} code={r.employeeCode} department={r.department} onEdit={editor.canEdit ? () => editor.open(r) : undefined} /> },
    { key: "in", label: "First Punch", width: "minmax(85px, 1fr)", align: "center", render: (r) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: r.isLate ? COLORS.warning : COLORS.ink }}>{fmtTime(firstPunchOf(r))}</Typography> },
    { key: "last", label: "Last Punch", width: "minmax(85px, 1fr)", align: "center", render: (r) => <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>{fmtTime(lastPunchOf(r))}</Typography> },
    { key: "timeline", label: "IN / OUT (click)", width: "minmax(165px, 1fr)", align: "center", render: (r) => <PunchSummary punches={middlePunches(r.punches)} /> },
    { key: "count", label: "Count", width: "minmax(95px, 1fr)", align: "center", render: (r) => <InOutCount inCount={r.inCount} outCount={r.outCount} /> },
    { key: "work", label: "Inside", width: "minmax(65px, 1fr)", align: "center", render: (r) => fmtMinutes(r.workMinutes) },
    { key: "outside", label: "Outside", width: "minmax(65px, 1fr)", align: "center", render: (r) => fmtMinutes(r.outsideMinutes) },
    { key: "late", label: "Late By", width: "minmax(65px, 1fr)", align: "center", render: (r) => (r.isLate ? <Typography sx={{ fontSize: 13, color: COLORS.warning, fontWeight: 600 }}>{fmtMinutes(r.lateMinutes)}</Typography> : "—") },
    { key: "early", label: "Early By", width: "minmax(65px, 1fr)", align: "center", render: (r) => (r.isEarly ? fmtMinutes(r.earlyMinutes) : "—") },
    { key: "status", label: "Status", width: "minmax(120px, 1fr)", align: "center", render: (r) => <StatusChip status={r.status} late={r.isLate} early={r.isEarly} /> },
  ];

  return (
    <DashboardLayout>
      <PageHeader
        title="Daily Report"
        subtitle={`${fmtDate(date, { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}${rules ? ` · Shift ${fmtClock(rules.shiftStart)} – ${fmtClock(rules.shiftEnd)}` : ""}`}
        actions={<ExportButton onClick={handleExport} busy={exporting || !data} />}
      />
      <AttendanceError error={error} onRetry={() => refetch()} retrying={isFetching} />

      <FlatCard>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.hairline}` }} alignItems={{ lg: "center" }}>
          {/* Date picker with prev / next */}
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Tooltip title="Previous day">
              <IconButton size="small" onClick={() => setDate((d) => shiftDate(d, -1))} sx={iconBtnSx}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <TextField type="date" size="small" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} inputProps={{ max: today }} sx={{ ...controlFieldSx, width: 170 }} />
            <Tooltip title="Next day">
              <span style={{ display: "inline-flex" }}>
                <IconButton size="small" disabled={date >= today} onClick={() => setDate((d) => shiftDate(d, 1))} sx={iconBtnSx}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <TextField size="small" placeholder="Search name, code, department…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ...fieldSx, flex: 1, minWidth: { lg: 220 } }} />
          <FilterChips
            value={filter}
            onChange={setFilter}
            options={[
              { key: "all", label: "All", count: counts.all },
              { key: "present", label: "Present", count: counts.present },
              { key: "half_day", label: "Half Day", count: counts.half_day },
              { key: "late", label: "Late", count: counts.late },
              { key: "early", label: "Early Out", count: counts.early },
              { key: "absent", label: "Absent", count: counts.absent },
              { key: "missed", label: "Missed Punch", count: counts.missed },
            ]}
          />
        </Stack>

        <DataTable
          columns={columns}
          rows={visible}
          loading={isLoading}
          emptyIcon={<EventNoteOutlinedIcon sx={{ fontSize: 40, color: COLORS.faint }} />}
          emptyText="No records for this date / filter"
          onRowClick={(r) => navigate(`/attendance/employee/${encodeURIComponent(r.employeeCode)}?month=${date.slice(0, 7)}`)}
        />
      </FlatCard>

      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: COLORS.faint }}>
          Punches are read in order — 1st = IN, 2nd = OUT, 3rd = IN … · repeat taps within 2 minutes are ignored · Inside = total time between each IN and its OUT.
        </Typography>
      </Box>
      {editor.dialog}
    </DashboardLayout>
  );
}

export default DailyReport;
